import { useAuth as useAuthKit } from '@workos-inc/authkit-react';
import { useMemo, type ReactNode } from 'react';

import { AuthContext } from './auth-context.js';
import { clearLastUsedOrgId } from './last-used-org-id.js';

/**
 * Publishes AuthKit's auth context value on the shared `AuthContext` so
 * `useAuth` consumers stay agnostic to which provider is mounted. Rendered
 * inside `<AuthKitProvider>` — `useAuthKit` requires that ancestor.
 *
 * Wraps `signOut` so the lastUsedOrgId localStorage value is cleared before
 * delegating, avoiding user-A-then-user-B-on-same-browser bleed (see
 * account-model design.md §7 "SPA-side defaulting via lastUsedOrgId").
 */
export function AuthKitBridge({ children }: { children: ReactNode }) {
  const auth = useAuthKit();
  const wrapped = useMemo(
    () => ({
      ...auth,
      // Cast to `typeof auth.signOut` so the wrapped function satisfies the
      // SDK's overload set (signOut has both void and Promise<void> overloads
      // depending on `navigate`).
      signOut: ((options?: Parameters<typeof auth.signOut>[0]) => {
        clearLastUsedOrgId();
        return auth.signOut(options as never);
      }) as typeof auth.signOut,
    }),
    [auth],
  );
  return <AuthContext.Provider value={wrapped}>{children}</AuthContext.Provider>;
}
